import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, DollarSign, Layers, Phone, Webhook, Map, Save, Download, Image as ImageIcon, Users } from "lucide-react";

const Section = ({ icon: Icon, title, desc, children, testid }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <h3 className="font-display font-bold text-sam-navy mb-1 flex items-center gap-2"><Icon className="h-4 w-4" />{title}</h3>
    {desc && <p className="text-xs text-sam-slate mb-3">{desc}</p>}
    {children}
  </div>
);

function PricingSection() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/postcard-pricing");
    setP({
      premium: data.per_spot.premium, standard: data.per_spot.standard, basic: data.per_spot.basic,
      full_cycle: data.full_cycle ?? "", annual: data.annual ?? "",
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        premium: Number(p.premium), standard: Number(p.standard), basic: Number(p.basic),
        full_cycle: p.full_cycle === "" ? null : Number(p.full_cycle),
        annual: p.annual === "" ? null : Number(p.annual),
      };
      await api.put("/admin/postcard-pricing", body);
      toast.success("Pricing saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!p) return <Loader2 className="h-4 w-4 animate-spin" />;
  const f = (k, label, hint) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`price-${k}`} />
      {hint && <p className="text-[11px] text-sam-slate mt-1">{hint}</p>}
    </div>
  );
  return (
    <Section icon={DollarSign} title="Pricing" desc="Per-spot prices are charged per monthly 10,000-home drop. Leave full-cycle & annual blank until you set them." testid="pc-pricing">
      <div className="grid sm:grid-cols-3 gap-3">
        {f("premium", "Premium 4×9 / drop")}
        {f("standard", "Standard 4×6 / drop")}
        {f("basic", "Basic 4×3 / drop")}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {f("full_cycle", "Full cycle (4 drops / 40k homes)", "Optional — blank = not offered")}
        {f("annual", "Annual (12 drops)", "Optional — blank = not offered")}
      </div>
      <Button className="mt-3 bg-sam-navy" onClick={save} disabled={busy} data-testid="save-pricing-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save pricing
      </Button>
    </Section>
  );
}

function PlansSection() {
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/postcard-plans");
    setPlans(data.plans);
  }, []);
  useEffect(() => { load(); }, [load]);
  const upd = (i, k, v) => setPlans(plans.map((pl, idx) => idx === i ? { ...pl, [k]: v } : pl));
  const save = async () => {
    setBusy(true);
    try {
      const clean = plans.map((pl) => ({
        ...pl, months: Number(pl.months) || 1,
        features: (Array.isArray(pl.features) ? pl.features : String(pl.features).split("\n")).map((x) => x.trim()).filter(Boolean),
      }));
      await api.put("/admin/postcard-plans", { plans: clean });
      toast.success("Plans saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!plans) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <Section icon={Layers} title="Commitment plans" desc="Plans ADD account features — they never discount the ad spot. Customer-facing name is always Saving America Money." testid="pc-plans">
      <div className="grid md:grid-cols-2 gap-3">
        {plans.map((pl, i) => (
          <div key={pl.key} className="rounded-lg border border-border p-3 space-y-2" data-testid={`plan-card-${pl.key}`}>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={pl.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`plan-name-${pl.key}`} /></div>
              <div><Label className="text-xs">Months</Label><Input type="number" value={pl.months} onChange={(e) => upd(i, "months", e.target.value)} data-testid={`plan-months-${pl.key}`} /></div>
            </div>
            <div><Label className="text-xs">Tagline</Label><Input value={pl.tagline || ""} onChange={(e) => upd(i, "tagline", e.target.value)} data-testid={`plan-tagline-${pl.key}`} /></div>
            <div>
              <Label className="text-xs">Features (one per line)</Label>
              <Textarea rows={5} value={(Array.isArray(pl.features) ? pl.features.join("\n") : pl.features) || ""} onChange={(e) => upd(i, "features", e.target.value)} data-testid={`plan-features-${pl.key}`} />
            </div>
          </div>
        ))}
      </div>
      <Button className="mt-3 bg-sam-navy" onClick={save} disabled={busy} data-testid="save-plans-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save plans
      </Button>
    </Section>
  );
}

function NumberAddonSection() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/number-addon");
    setCfg({ price: data.price ?? "", grace_days: data.grace_days ?? 14, records: data.records || [] });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/number-addon", { price: cfg.price === "" ? "" : Number(cfg.price), grace_days: Number(cfg.grace_days) });
      toast.success("Number add-on saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <Section icon={Phone} title="Bonus number add-on" desc="When an 8/12-month plan lapses, the bonus phone number is offered as a paid monthly add-on during a grace window." testid="pc-number-addon">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Monthly price (blank = not offered)</Label><Input type="number" value={cfg.price} onChange={(e) => setCfg({ ...cfg, price: e.target.value })} data-testid="addon-price" /></div>
        <div><Label className="text-xs">Grace days</Label><Input type="number" value={cfg.grace_days} onChange={(e) => setCfg({ ...cfg, grace_days: e.target.value })} data-testid="addon-grace" /></div>
      </div>
      <Button className="mt-3 bg-sam-navy" onClick={save} disabled={busy} data-testid="save-addon-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save add-on
      </Button>
      {cfg.records.length > 0 && (
        <p className="text-xs text-sam-slate mt-2">{cfg.records.length} active / grace record(s).</p>
      )}
    </Section>
  );
}

function WebhooksSection() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/webhooks");
    setCfg({ endpoint: data.endpoint || "", events: data.events || [], recent: data.recent || [] });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/webhooks", { endpoint: cfg.endpoint }); toast.success("Webhook saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <Section icon={Webhook} title="Automation webhook" desc="Outbound events (slot held/purchased/released, ad submitted/approved, card full, payments, forfeits) POST to this URL." testid="pc-webhooks">
      <div className="flex gap-2 items-end">
        <div className="flex-1"><Label className="text-xs">Endpoint URL</Label><Input value={cfg.endpoint} onChange={(e) => setCfg({ ...cfg, endpoint: e.target.value })} placeholder="https://your-crm.example.com/sam/webhook" data-testid="webhook-endpoint" /></div>
        <Button className="bg-sam-navy" onClick={save} disabled={busy} data-testid="save-webhook-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">{cfg.events.map((e) => <span key={e} className="text-[10px] font-mono bg-slate-100 text-sam-slate px-1.5 py-0.5 rounded">{e}</span>)}</div>
      {cfg.recent.length > 0 && (
        <div className="mt-3 text-xs">
          <div className="font-semibold text-sam-navy mb-1">Recent deliveries</div>
          <div className="space-y-1 max-h-40 overflow-auto" data-testid="webhook-recent">
            {cfg.recent.slice(0, 15).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 border-b border-border/60 pb-0.5">
                <span className="font-mono">{r.event}</span>
                <span className={r.delivered ? "text-sam-green" : "text-slate-400"}>{r.delivered ? "✓ sent" : (r.status || "logged")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function RouteSection() {
  const [runs, setRuns] = useState([]);
  const [rid, setRid] = useState("");
  const [sched, setSched] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rep, setRep] = useState("");
  const [evalRes, setEvalRes] = useState(null);
  useEffect(() => { api.get("/postcard-runs").then(({ data }) => setRuns(data)); }, []);
  const loadRoute = useCallback(async (id) => {
    setSched(null); setForm(null);
    const { data } = await api.get(`/admin/postcard-runs/${id}/schedule`);
    setSched(data);
    const byKey = Object.fromEntries((data.quadrants || []).map((q) => [q.key, q]));
    setForm({
      radius_miles: data.radius_miles ?? 3,
      cycle_start: data.cycle_start || "",
      quads: ["TR", "TL", "BL", "BR"].map((k) => ({
        key: k, label: byKey[k]?.label || k,
        zips: (byKey[k]?.zips || []).join(", "), households: byKey[k]?.households || 0,
      })),
    });
  }, []);
  const onSelect = (id) => { setRid(id); setEvalRes(null); const r = runs.find((x) => x.id === id); setRep((r && r.assigned_rep) || ""); loadRoute(id); };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/postcard-runs/${rid}/route`, {
        radius_miles: Number(form.radius_miles),
        cycle_start: form.cycle_start || null,
        quadrants: form.quads.map((q) => ({ key: q.key, zips: String(q.zips).split(",").map((z) => z.trim()).filter(Boolean), households: Number(q.households) || 0 })),
      });
      toast.success("Route saved");
      await loadRoute(rid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const updQ = (i, k, v) => setForm({ ...form, quads: form.quads.map((q, idx) => idx === i ? { ...q, [k]: v } : q) });
  const saveRep = async () => { try { await api.patch(`/admin/postcard-runs/${rid}/assign-rep`, { referly_code: rep }); toast.success("Rep assigned"); } catch { toast.error("Failed"); } };
  const evalMail = async () => { try { const { data } = await api.post(`/admin/postcard-runs/${rid}/evaluate-mail`); setEvalRes(data); toast.success(data.status === "ready" ? "Card is ready to mail" : "Card held"); } catch { toast.error("Failed"); } };
  const totalHouseholds = form ? form.quads.reduce((s, q) => s + (Number(q.households) || 0), 0) : 0;
  return (
    <Section icon={Map} title="Route & quadrants" desc="A route centers on one ZIP; the radius is split into four quadrants mailed in order TR → TL → BL → BR, ~10,000 homes each drop." testid="pc-route">
      <div className="max-w-sm">
        <Label className="text-xs">Route</Label>
        <Select value={rid} onValueChange={onSelect}>
          <SelectTrigger data-testid="route-select"><SelectValue placeholder="Select a postcard route" /></SelectTrigger>
          <SelectContent className="max-h-64">{runs.map((r) => <SelectItem key={r.id} value={r.id}>{r.route_code} · {r.city}, {r.state_abbr} {r.central_zip ? `(${r.central_zip})` : ""}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {form && (
        <div className="mt-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3 max-w-md">
            <div><Label className="text-xs">Radius (miles)</Label><Input type="number" step="0.1" value={form.radius_miles} onChange={(e) => setForm({ ...form, radius_miles: e.target.value })} data-testid="route-radius" /></div>
            <div><Label className="text-xs">Cycle start (YYYY-MM)</Label><Input value={form.cycle_start} onChange={(e) => setForm({ ...form, cycle_start: e.target.value })} placeholder="2026-06" data-testid="route-cycle-start" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {form.quads.map((q, i) => (
              <div key={q.key} className="rounded-lg border border-border p-3" data-testid={`quad-${q.key}`}>
                <div className="font-semibold text-sm text-sam-navy mb-2">{q.label} <span className="font-mono text-xs text-sam-slate">({q.key})</span></div>
                <Label className="text-xs">ZIPs / carrier routes (comma-separated)</Label>
                <Input value={q.zips} onChange={(e) => updQ(i, "zips", e.target.value)} placeholder="65708, 65712" data-testid={`quad-zips-${q.key}`} />
                <Label className="text-xs mt-2 block">Households</Label>
                <Input type="number" value={q.households} onChange={(e) => updQ(i, "households", e.target.value)} data-testid={`quad-households-${q.key}`} />
              </div>
            ))}
          </div>
          <div className="text-sm text-sam-slate" data-testid="route-total-households">Total households across all quadrants: <span className="font-bold text-sam-navy">{totalHouseholds.toLocaleString()}</span></div>
          <Button className="bg-sam-navy" onClick={save} disabled={busy} data-testid="save-route-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save route</Button>
          <a href={`${API}/postcard-runs/${rid}/export.pdf`} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 rounded-md border border-sam-navy/30 px-3 py-2 text-sm font-semibold text-sam-navy hover:bg-sam-navy/5" data-testid="download-print-pdf">
            <Download className="h-4 w-4" />Download print-ready PDF (current drop)
          </a>
          {rid && (
            <div className="mt-4 rounded-lg border border-border p-3 space-y-2" data-testid="route-rep-mail">
              <div className="flex items-end gap-2 flex-wrap">
                <div><Label className="text-xs">Assigned rep (Referly code)</Label><Input value={rep} onChange={(e) => setRep(e.target.value)} placeholder="e.g. JANE20" className="w-44" data-testid="route-assign-rep" /></div>
                <Button size="sm" variant="outline" onClick={saveRep} data-testid="route-save-rep-btn">Save rep</Button>
                <Button size="sm" variant="outline" onClick={evalMail} data-testid="route-eval-mail-btn">Evaluate mailing now</Button>
              </div>
              <p className="text-[11px] text-sam-slate">Mails only if 10+/12 spots are paid AND paid ad sales are $10,500+ by the deadline. Open spots on a ready card auto-fill with house ads; if either minimum is missed the card is held and paid advertisers carry over to the next drop.</p>
              {evalRes && <div className={`text-xs font-semibold ${evalRes.status === "ready" ? "text-sam-green" : "text-sam-crimson"}`} data-testid="route-eval-result">{evalRes.status === "ready" ? `Ready to mail — ${evalRes.paid}/12 paid, $${(evalRes.paid_amount || 0).toLocaleString()} in sales${evalRes.house_fills?.length ? `, ${evalRes.house_fills.length} house-ad fill(s)` : ""}${evalRes.payout_date ? ` · commissions pay ${new Date(evalRes.payout_date).toLocaleDateString(undefined, { timeZone: "UTC" })}` : ""}` : `HELD — ${(evalRes.reasons || []).join("; ")}. Needs 10+ paid spots and $10,500+ in sales. Paid advertisers carry over to the next drop.`}</div>}
            </div>
          )}
          {sched?.schedule && (
            <div className="mt-2">
              <div className="font-semibold text-sm text-sam-navy mb-1">Next 12 drops</div>
              <div className="flex flex-wrap gap-1.5" data-testid="route-schedule">
                {sched.schedule.map((s) => {
                  const isCur = sched.current && s.month === sched.current.month;
                  return <span key={s.month} className={`text-[11px] px-2 py-0.5 rounded font-mono ${isCur ? "bg-sam-crimson text-white" : "bg-slate-100 text-sam-slate"}`}>{s.month} {s.quadrant}</span>;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function PendingAdsSection() {
  const [ads, setAds] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/admin/pending-ads"); setAds(data); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (sid, kind) => {
    try {
      if (kind === "approve") await api.post(`/admin/postcard-slots/${sid}/approve-ad`);
      else { const reason = window.prompt("Reason for rejection (optional):") || ""; await api.post(`/admin/postcard-slots/${sid}/reject-ad`, { reason }); }
      toast.success(kind === "approve" ? "Ad approved — it will now print & show live" : "Ad rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!ads) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <Section icon={ImageIcon} title="Ad proofs awaiting approval" desc="Only approved ads appear on live ZIP pages and in the print-ready PDF." testid="pc-pending-ads">
      {ads.length === 0 ? <p className="text-sm text-sam-slate">No ads waiting for review.</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {ads.map((s) => (
            <div key={s.id} className="rounded-lg border border-border p-3 flex gap-3" data-testid={`pending-ad-${s.id}`}>
              {s.ad_asset_id && <img src={`${API}/ad-assets/${s.ad_asset_id}/file`} alt="ad proof" className="h-20 w-28 object-contain bg-slate-50 rounded border" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-sam-navy truncate">{s.business_name}</div>
                <div className="text-xs text-sam-slate">{s.slot_type} · {s.category}</div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" className="bg-sam-green hover:bg-sam-green/90 h-7 text-xs" onClick={() => act(s.id, "approve")} data-testid={`approve-ad-${s.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(s.id, "reject")} data-testid={`reject-ad-${s.id}`}>Reject</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function HouseAdsSection() {
  const [ads, setAds] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get("/admin/house-ads"); setAds(data); }, []);
  useEffect(() => { load(); }, [load]);
  const upd = (i, k, v) => setAds(ads.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const save = async () => { setBusy(true); try { await api.put("/admin/house-ads", { ads }); toast.success("House ads saved"); } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); } };
  if (!ads) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <Section icon={ImageIcon} title="House ads (auto-fill)" desc="When a card meets both minimums (10+ of 12 spots paid and $10,500+ in sales), any open spots auto-fill with Saving America Money house ads. House-ad spots earn no commission." testid="pc-house-ads">
      <div className="grid md:grid-cols-2 gap-3">
        {ads.map((a, i) => (
          <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`house-ad-${i}`}>
            <div className="text-xs font-semibold text-sam-navy">Open-spot #{a.slot || i + 1}</div>
            <div><Label className="text-xs">Headline</Label><Input value={a.headline} onChange={(e) => upd(i, "headline", e.target.value)} data-testid={`house-headline-${i}`} /></div>
            <div><Label className="text-xs">Copy</Label><Textarea rows={2} value={a.copy} onChange={(e) => upd(i, "copy", e.target.value)} data-testid={`house-copy-${i}`} /></div>
            <div><Label className="text-xs">Link</Label><Input value={a.link} onChange={(e) => upd(i, "link", e.target.value)} data-testid={`house-link-${i}`} /></div>
            <div><Label className="text-xs">Image URL (optional)</Label><Input value={a.image_url} onChange={(e) => upd(i, "image_url", e.target.value)} data-testid={`house-image-${i}`} /></div>
          </div>
        ))}
      </div>
      <Button className="mt-3 bg-sam-navy" onClick={save} disabled={busy} data-testid="save-house-ads-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save house ads</Button>
    </Section>
  );
}

function RepAssignmentSection() {
  const [runs, setRuns] = useState(null);
  const [edits, setEdits] = useState({});
  useEffect(() => {
    api.get("/postcard-runs").then(({ data }) => {
      setRuns(data);
      setEdits(Object.fromEntries(data.map((r) => [r.id, r.assigned_rep || ""])));
    });
  }, []);
  const save = async (rid) => {
    try {
      await api.patch(`/admin/postcard-runs/${rid}/assign-rep`, { referly_code: edits[rid] || "" });
      toast.success(edits[rid] ? `Rep ${edits[rid]} assigned` : "Rep cleared");
      setRuns((rs) => rs.map((r) => (r.id === rid ? { ...r, assigned_rep: edits[rid] || "", assigned_rep_source: edits[rid] ? "manual" : null } : r)));
    } catch { toast.error("Failed to assign rep"); }
  };
  const [zipEdits, setZipEdits] = useState({});
  const saveZip = async (rid) => {
    try {
      const { data } = await api.patch(`/admin/postcard-runs/${rid}/route`, { central_zip: zipEdits[rid] || "" });
      toast.success(data.central_zip ? `ZIP ${data.central_zip} set — ZIP page is live` : "ZIP cleared");
      setRuns((rs) => rs.map((r) => (r.id === rid ? { ...r, central_zip: data.central_zip } : r)));
      setZipEdits((z) => { const n = { ...z }; delete n[rid]; return n; });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set ZIP"); }
  };
  if (!runs) return null;
  const unassigned = runs.filter((r) => !r.assigned_rep).length;
  return (
    <Section icon={Users} title="Reps per run" desc="Assign the Referly code of the rep who owns each active postcard run. This drives commissions, the Monday leaderboard and renewal ownership." testid="pc-rep-assignment">
      {unassigned > 0 && <div className="mb-3 rounded-md bg-sam-crimson/10 border border-sam-crimson/30 px-3 py-2 text-xs font-semibold text-sam-crimson" data-testid="rep-unassigned-warning">{unassigned} run{unassigned > 1 ? "s" : ""} still need a rep — sales on these won't attribute commission until assigned.</div>}
      {runs.length === 0 ? <p className="text-sm text-sam-slate">No postcard runs yet.</p> : (
        <div className="space-y-2">
          {runs.map((r) => {
            const dirty = (edits[r.id] ?? "") !== (r.assigned_rep || "");
            return (
              <div key={r.id} className="flex items-center justify-between gap-3 flex-wrap border-b border-border/50 pb-2" data-testid={`rep-run-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-sam-navy">{r.route_code} · {r.city}, {r.state_abbr} {r.central_zip ? `(${r.central_zip})` : ""}
                    {r.customer_started && !r.assigned_rep && <span className="ml-2 rounded bg-sam-crimson px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`rep-run-needs-rep-${r.id}`}>NEEDS A REP</span>}
                    {r.customer_started && <span className="ml-2 text-[10px] font-normal text-sam-slate" data-testid={`rep-run-started-${r.id}`}>started by {r.started_by_business || "a customer"}</span>}
                  </div>
                  <div className="text-[11px] text-sam-slate">{r.assigned_rep ? <>Current rep: <span className="font-mono text-sam-navy">{r.assigned_rep}</span>{r.assigned_rep_source && <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-sam-slate" data-testid={`rep-run-source-${r.id}`}>{{ zip_territory: "auto · area ZIP", creator: "auto · creator", manual: "set manually" }[r.assigned_rep_source]}</span>}</> : <span className="text-sam-crimson font-semibold">No rep assigned</span>} · {r.sold_count ?? 0} sold</div>
                </div>
                <div className="flex items-center gap-2">
                  <Input value={zipEdits[r.id] ?? r.central_zip ?? ""} onChange={(e) => setZipEdits({ ...zipEdits, [r.id]: e.target.value })} placeholder="Central ZIP" className="w-28 h-8 text-xs" data-testid={`rep-run-zip-input-${r.id}`} />
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={(zipEdits[r.id] ?? r.central_zip ?? "") === (r.central_zip || "")} onClick={() => saveZip(r.id)} data-testid={`rep-run-zip-save-${r.id}`}>Set ZIP</Button>
                  <Input value={edits[r.id] ?? ""} onChange={(e) => setEdits({ ...edits, [r.id]: e.target.value })} placeholder="Referly code e.g. JANE20" className="w-44 h-8 text-xs" data-testid={`rep-run-input-${r.id}`} />
                  <Button size="sm" variant={dirty ? "default" : "outline"} className={`h-8 text-xs ${dirty ? "bg-sam-navy" : ""}`} disabled={!dirty} onClick={() => save(r.id)} data-testid={`rep-run-save-${r.id}`}>Save</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

export function PostcardSettings() {
  return (
    <div className="space-y-5" data-testid="postcard-settings">
      <RepAssignmentSection />
      <PendingAdsSection />
      <PricingSection />
      <PlansSection />
      <RouteSection />
      <HouseAdsSection />
      <NumberAddonSection />
      <WebhooksSection />
    </div>
  );
}

export default PostcardSettings;
